import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled
} from '@mui/material'
import { FC, useState } from 'react'
import { CustomModal } from '../../common/CustomModal'
import { formatCurrency } from '../../../utils'
import { PayableType } from './constants'
import { PAYABLE_TYPE_LABELS } from './constants'

const PAYABLE_DESCRIPTION: Record<PayableType, string> = {
  [PayableType.TRUST]:
    'Money is moving out! This payable will use the Trust (IOLTA) bank account and the associated GL account. Account balance and ledger will be updated if marked as paid.',
  [PayableType.ESCROW]:
    'Money is moving out! This payable will use the Escrow bank account and the associated GL account. Account balance and ledger will be updated if marked as paid.',
  [PayableType.OTHER]:
    'Money is moving out! This payable will use the selected bank and GL accounts. Account balance and ledger will be updated if marked as paid.'
}

const DetailTable = styled(Table)({
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: '12px 16px',
    fontSize: '14px'
  },
  '& .MuiTableRow-root > .MuiTableCell-root:first-of-type': {
    borderRight: '1px solid #E0E0E0',
    fontWeight: 600,
    width: '40%',
    backgroundColor: '#FAFAFA'
  }
})

export interface PayableConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  loading: boolean
  payableType: PayableType
  bankAccountLabel: string
  vendorLabel: string
  amount: number
  paymentMethod: string
  checkOrRefNo: string
  invoiceDate: string
  payDate: string
  markAsPaid: boolean
  notes?: string
}

const PayableConfirmationModal: FC<PayableConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  payableType,
  bankAccountLabel,
  vendorLabel,
  amount,
  paymentMethod,
  checkOrRefNo,
  invoiceDate,
  payDate,
  markAsPaid,
  notes
}) => {
  const [confirmed, setConfirmed] = useState(false)

  const handleClose = () => {
    setConfirmed(false)
    onClose()
  }

  const details = [
    { label: 'Payable Type', value: PAYABLE_TYPE_LABELS[payableType] },
    { label: 'Bank Account', value: bankAccountLabel || '—' },
    { label: 'Vendor', value: vendorLabel || '—' },
    { label: 'Amount', value: formatCurrency(amount) },
    { label: 'Payment Method', value: paymentMethod.replace(/CHEQUE/g, 'CHECK') || '—' },
    { label: 'Check / Reference No', value: checkOrRefNo || '—' },
    { label: 'Invoice Date', value: invoiceDate || '—' },
    { label: 'Pay Date', value: payDate || '—' },
    { label: 'Mark as Paid', value: markAsPaid ? 'Yes' : 'No' },
    ...(notes ? [{ label: 'Notes', value: notes }] : [])
  ]

  return (
    <CustomModal title="Confirm Payable" isOpen={isOpen} handleClose={handleClose} maxWidth="md">
      <Box>
        <Typography fontSize={14} mb={2} color="text.secondary">
          Please review the following details carefully before submitting this payable.
        </Typography>

        <TableContainer>
          <DetailTable>
            <TableBody>
              {details.map(({ label, value }) => (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DetailTable>
        </TableContainer>

        <Box mt={2} p={2} sx={{ backgroundColor: '#FFF8E1', borderRadius: 1, border: '1px solid #FFE082' }}>
          <Typography fontSize={13} color="#5D4037" fontWeight={500}>
            {PAYABLE_DESCRIPTION[payableType]}
          </Typography>
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography fontSize={13} fontWeight={500}>
                I have reviewed and confirmed the details above are correct
              </Typography>
            }
          />
        </Box>
      </Box>

      <DialogActions sx={{ padding: '20px 0 0' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!confirmed || loading}
          onClick={onConfirm}
          endIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          Submit
        </Button>
      </DialogActions>
    </CustomModal>
  )
}

export default PayableConfirmationModal
